import type {SystemProps} from '@chakra-ui/styled-system';
import {createMultiStyleConfigHelpers, defineStyle} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {tokens as dark} from '../../../generated/dark';
import {tokens as light} from '../../../generated/light';

import {sidebarAnatomy} from './anatomy';
import {footerVars, headerVars, itemVars, menuVars, vars} from './css-vars';

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(sidebarAnatomy.keys());

const lightTokens = light.components.nav;
const darkTokens = dark.components.nav;
const sizing = light.components.navigation;
const semantic = light.semantic;

const container = defineStyle(props => {
  const background = mode(lightTokens.background, darkTokens.background)(props);

  return {
    // Global
    [vars.background.variable]: `colors.${background}`,
    [vars[`padding-horizontal`].variable]: `space.${sizing[`side-padding`]}`,
    [vars[`group-button-font-weight`].variable]: `fontWeights.medium`,
    [vars[`button-font-weight`].variable]: `fontWeights.normal`,
    [vars[`icon-size`].variable]: `space.space-250`,
    [vars[`font-size`].variable]: `fontSizes.md`,
    [vars[`text-space`].variable]: `8px`, // TODO: Tokens like space-050 do not work, only static values
    // Item
    [itemVars.gap.variable]: `space.${sizing[`item-space-between`]}`,
    [itemVars.radius.variable]: `radii.radius-100`,
    [itemVars.padding.variable]: `space.${sizing[`item-padding`]}`,
    // Header
    [headerVars.height.variable]: `space.space-400`,
    [headerVars.padding.variable]: `space.space-200`,
    // Footer
    [footerVars[`padding-horizontal`].variable]: `space.space-250`,
    [footerVars.gap.variable]: `space.space-250`,
    // Menu
    [menuVars.gap.variable]: `space.${semantic.spacing.gutter.xs}`,

    // Layout
    display: `flex`,
    flexDirection: `column`,
    flexShrink: 0,
    alignItems: `flex-start`,
    gap: semantic.spacing.gutter[`page-default-vertical`],

    // Spacing
    pt: semantic.spacing.margins.top,
    pb: semantic.spacing.margins.bottom,

    minWidth: `240px`,
    bgColor: background,
  } satisfies SystemProps;
});

const header = defineStyle({
  display: `flex`,
  flexShrink: 0,
  alignSelf: `stretch`,
  alignItems: `center`,
  height: headerVars.height.reference,
  padding: headerVars.padding.reference,
});

const menu = defineStyle({
  display: `flex`,
  flexDirection: `column`,
  flex: `1 0 0`,
  alignSelf: `stretch`,
  alignItems: `flex-start`,
  gap: menuVars.gap.reference,
  px: vars[`padding-horizontal`].reference,
});

const menuGroup = defineStyle(() => {
  const {px: _px, ...rest} = menu;
  return rest;
});

const menuItem = defineStyle(props => {
  const background = {
    default: `transparent`,
    active: mode(lightTokens[`background-selected`], darkTokens[`background-selected`])(props),
    hover: mode(lightTokens[`background-hover`], darkTokens[`background-hover`])(props),
    pressed: mode(lightTokens[`background-pressed`], darkTokens[`background-pressed`])(props),
    expended: mode(lightTokens[`background-selected`], darkTokens[`background-selected`])(props),
  };
  const text = {
    default: mode(lightTokens[`text-default`], darkTokens[`text-default`])(props),
    active: mode(lightTokens[`text-active`], darkTokens[`text-active`])(props),
    opened: mode(lightTokens[`text-selected-2ndlevel`], darkTokens[`text-selected-2ndlevel`])(props),
  };

  return {
    display: `flex`,
    borderRadius: itemVars.radius.reference,
    alignSelf: `stretch`,
    alignItems: `center`,
    justifyContent: `flex-start`,

    padding: itemVars.padding.reference,
    bg: background.default,
    color: text.default,
    height: `auto`,

    // Reset
    [`&&`]: {
      textDecoration: `none`, // Sometimes we render <a> tag
      color: `inherit`,
    },
    [`&[data-collapsible]`]: {},
    [`&[data-active-descendant]`]: {
      bg: background.default,
      fontWeight: 600,
    },
    [`&&[data-current]`]: {
      color: text.active,
      bg: background.active,
      _hover: {
        bg: background.active,
      },
      _active: {
        bg: background.active,
      },
    },
    _active: {
      bg: background.pressed,
    },
    _hover: {
      bg: background.hover,
    },
  };
});

const icon = defineStyle({
  width: vars[`icon-size`].reference,
  height: vars[`icon-size`].reference,
  p: `0.375rem`,
  marginTop: `-1px`,
  mr: `space-150`,
  fontSize: vars[`icon-size`].reference,
  marginInlineEnd: 0,
});

const footer = defineStyle({
  px: footerVars[`padding-horizontal`].reference,
  display: `flex`,
  flexDirection: `column`,
  alignItems: `flex-start`,
  alignSelf: `stretch`,
  gap: `space-300`,
});

const baseStyle = definePartsStyle(props => ({
  container: container(props),
  [`menu-item`]: menuItem(props),
  'menu-group': menuGroup(),
  icon,
  header,
  footer,
  menu,
}));

export const sidebarTheme = defineMultiStyleConfig({
  baseStyle,
});
